import { message, Typography } from 'antd'
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint'
import api from 'api'
import { DefaultLayout } from 'components/layouts/DefaultLayout'
import { ArtWorkFeatures } from 'components/models/artworks/ArtWorkFilterForm/ArtWorkFeatures'
import { GotoTopIcon } from 'components/models/artworks/ArtWorkFilterForm/GotoTopIcon'
import { ArtWorks } from 'components/models/artworks/ArtWorks'
import { NextHead } from 'components/shared/NextHead'
import { useArtistNames } from 'hooks/artists'
import { useArtworks } from 'hooks/artworks'
import { first } from 'lodash'
import { GetServerSideProps } from 'next'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { NextPageWithHead } from 'pages/_app'
import { useMemo } from 'react'

type Props = {
  topArtWork: ArtWork | null
}

const Page: NextPageWithHead<Props> = () => {
  const { xs } = useBreakpoint()
  const { t } = useTranslation()
  const router = useRouter()
  const breadCrumbs = [
    {
      text: t('ホーム'),
      href: '/',
    },
    {
      text: t('作品一覧'),
    },
  ]

  const searchObject = useMemo(() => {
    const params = new URLSearchParams(router.asPath.split(/\?/)[1])
    const searchObject = {}

    for (const key of Array.from(params.keys())) {
      if (key.includes('_in')) {
        Object.assign(searchObject, { [key]: params.getAll(key) })
      } else {
        Object.assign(searchObject, { [key]: params.get(key) })
      }
    }
    return searchObject
  }, [router.asPath])

  const sortObject = useMemo(() => {
    const params = new URLSearchParams(router.asPath.split(/\?/)[1])
    const sortObject = {}

    for (const key of Array.from(params.keys())) {
      if (key.includes('sort_')) {
        Object.assign(sortObject, { [key]: params.get(key) })
      }
    }
    return sortObject
  }, [router.asPath])

  const ART_WORK_LIMIT = 18
  const {
    data,
    error: errorArtworks,
    isValidating: isValidatingArtworks,
    setSize: setSizeArtworks,
  } = useArtworks(searchObject, ART_WORK_LIMIT, sortObject)
  const artworks = data?.flatMap((v) => v.artWorksData) || []

  const { data: artists = [] } = useArtistNames()

  if (errorArtworks) {
    message.error(errorArtworks.error.data.error)
    return <></>
  }

  return (
    <DefaultLayout onlyLogo={false} breadCrumbs={breadCrumbs}>
      <div style={{ padding: xs ? '40px 16px 0 16px' : '40px 64px 0 64px' }}>
        <div
          id="section-top"
          style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'flex-start',
            paddingBottom: '24px',
            borderBottom: '1px solid var(--ax-gray-3, #C4C4C4)',
          }}
        >
          <Typography.Title
            style={{ fontSize: '32px', fontWeight: 'normal', lineHeight: 1 }}
          >
            {t('Artworks')}
          </Typography.Title>
          <Typography.Title
            level={2}
            style={{
              fontSize: '14px',
              fontWeight: 'normal',
              lineHeight: 1,
              marginLeft: '8px',
            }}
          >
            / {t('作品一覧')}
          </Typography.Title>
        </div>
        <ArtWorkFeatures xs={xs} />

        <ArtWorks
          artWorks={artworks}
          isValidatingArtworks={isValidatingArtworks}
          setSizeArtworks={setSizeArtworks}
          artWorkTotalCount={first(data)?.xTotalCount || 0}
          xs={xs}
          artists={artists}
        />

        <div style={{ padding: xs ? '80px' : '120px' }} />
        {xs && <GotoTopIcon href={'section-top'} />}
      </div>
    </DefaultLayout>
  )
}

export default Page

Page.getHead = ({ topArtWork }) => {
  return (
    <NextHead
      title="アート作品一覧｜ArtX"
      type="website"
      thumbnailUrl={topArtWork?.main_image_thumb_contain_url}
      description="ギャラリーから出展されるアート作品を紹介します。"
    />
  )
}

export const getServerSideProps: GetServerSideProps<Props> = async ({
  locale,
}) => {
  const { data: artWorks } = await api.get<ArtWork[]>(
    '/home/art_works?limit=10',
  )

  return {
    props: {
      // NOTE: 画像を設定していない作品があるため、その場合は画像を設定している最初の作品を設定する
      topArtWork:
        artWorks.find((v) => !!v.main_image_thumb_contain_url) || null,
      ...(await serverSideTranslations(locale ?? 'ja', ['common'])),
    },
  }
}
